import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import { BoutonSecondaire, Champ, Etiquette, Selection } from './ui/Champ';
import { MessageErreur } from './ui/MessageEtat';
import { erreurGlobale } from './ui/erreurs-champs';
import { useToast } from '../../context/ToastContext';

/**
 * Régularisation annuelle des indemnités kilométriques (R-IK-06).
 *
 * Le barème s'applique au kilométrage ANNUEL, mais on paie au fil de l'eau :
 * les deux ne coïncident que par chance. Cet écran montre l'écart par salarié
 * et permet de l'émettre en dépense.
 *
 * L'écart joue dans les DEUX sens, et c'est contre-intuitif : la tranche
 * médiane porte un coefficient plus bas que la première, compensé par une part
 * fixe. Un salarié qui la franchit de peu a donc pu être surpayé.
 */
export function RegularisationIk() {
  const toast = useToast();
  const qc = useQueryClient();
  const annees = Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - i);
  const [annee, setAnnee] = useState(annees[0]!);

  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'regularisation', annee],
    queryFn: () => ndfApi.trajets.regularisation(annee),
    retry: false,
  });

  const emettre = useMutation({
    mutationFn: (salarieId: string) => ndfApi.trajets.regulariser(salarieId, annee),
    onSuccess: () => {
      toast.success('Dépense de régularisation créée, en brouillon.');
      qc.invalidateQueries({ queryKey: ['ndf', 'regularisation'] });
      qc.invalidateQueries({ queryKey: ['ndf', 'depenses'] });
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'La régularisation a échoué.'),
  });

  const lignes = data?.regularisations ?? [];

  return (
    <div className="space-y-3">
      <p className="text-sm text-nuit/70">
        Le barème kilométrique s’applique au total de l’année, alors que les trajets sont payés au
        fil de l’eau. Un salarié qui franchit une tranche en cours d’année a donc été payé au
        mauvais taux pendant des mois : dans un sens ou dans l’autre.
      </p>

      <Champ label="Année">
        <Selection value={annee} onChange={(e) => setAnnee(Number(e.target.value))}>
          {annees.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </Selection>
      </Champ>

      {isLoading && <p className="text-sm text-nuit/60">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}
      {!isLoading && lignes.length === 0 && (
        <p className="text-sm text-nuit/70">Aucun trajet déclaré en {annee}.</p>
      )}

      <ul className="space-y-2">
        {lignes.map((l) => {
          const aRegulariser = !l.erreur && Math.abs(l.ecart ?? 0) >= 0.01;
          return (
            <li key={l.salarie_id} className="rounded-lg border border-nuit/10 bg-white p-3 text-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-titre font-bold text-nuit">{l.salarie}</span>
                {l.erreur ? (
                  <Etiquette ton="alerte">Calcul impossible</Etiquette>
                ) : aRegulariser ? (
                  <Etiquette ton={(l.ecart ?? 0) > 0 ? 'attention' : 'alerte'}>
                    {(l.ecart ?? 0) > 0 ? 'Complément dû' : 'Trop-perçu'}
                  </Etiquette>
                ) : (
                  <Etiquette ton="positif">À jour</Etiquette>
                )}
              </div>

              {l.erreur ? (
                <p className="mt-1 text-corail">{l.erreur}</p>
              ) : (
                <>
                  <dl className="mt-1 space-y-1">
                    <Ligne libelle="Kilomètres déclarés" valeur={`${l.total_km} km`} />
                    <Ligne libelle="Déjà versé" valeur={euros(l.verse ?? 0)} />
                    <Ligne libelle="Barème appliqué au total annuel" valeur={euros(l.theorique ?? 0)} />
                    <Ligne libelle="Écart" valeur={euros(l.ecart ?? 0)} />
                  </dl>

                  {/* Sans cette réserve, on émettrait un rappel de salaire faux :
                      Gertrude ignore ce qui a été versé avant la reprise. */}
                  {l.fiable === false && (
                    <p className="mt-2 rounded-md border border-jaune-fond/60 border-l-4 border-l-jaune-fond p-2 text-jaune">
                      Ce salarié a un kilométrage repris d’un autre système. L’écart affiché ne tient
                      pas compte de ce qui lui a déjà été versé pour ces kilomètres : la
                      régularisation doit être calculée à la main.
                    </p>
                  )}

                  {aRegulariser && l.fiable !== false && (
                    <div className="mt-2">
                      <BoutonSecondaire
                        type="button"
                        disabled={emettre.isPending}
                        onClick={() => emettre.mutate(l.salarie_id)}
                      >
                        {(l.ecart ?? 0) > 0
                          ? 'Créer la dépense de complément'
                          : 'Créer la dépense de trop-perçu'}
                      </BoutonSecondaire>
                    </div>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function euros(v: number) {
  return v.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

function Ligne({ libelle, valeur }: { libelle: string; valeur: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-nuit/5 pb-1 last:border-0">
      <dt className="text-nuit/60">{libelle}</dt>
      <dd className="text-right font-medium text-nuit">{valeur}</dd>
    </div>
  );
}
